import { CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import AddPaymentInfoForm, { createEmptyPaymentInfo, PaymentInfoFormData } from '@/pages/Supporteds/AddPaymentInfoPopup/AddPaymentInfoForm';
import popupStyles from '@/pages/Supporteds/AddPaymentInfoPopup/AddPaymentInfoPopup.module.scss';
import { usePostAuthApi, usePutAuthApi } from '@/hooks/UseApiCalls';
import { getShortRequestId } from '@/helpers/requestIdHelper';
import { getAggregatedStatus, getStatusColor, getStatusGroup } from '@/helpers/requestStatus';
import { PaymentMethodType } from '@/models/common/PaymentMethodType';
import { BankAccountDtoIn } from '@/models/in/BankAccountDtoIn';
import { PaymentMethodsDtoIn } from '@/models/in/PaymentMethodsDtoIn';
import { BankAccountDetailsDtoOut } from '@/models/out/BankAccountDetailsDtoOut';
import { ReturnedPaymentDtoIn, ReturnedPaymentFixResultDtoIn, ReturnedPaymentMonthDtoIn } from '@/models/in/ReturnedPaymentDtoIn';
import successIcon from '@/assets/icons/success.svg';
import BankAccountLine from './BankAccountLine';
import { cancelReturnedPaymentUrl, fixReturnedPaymentUrl } from './useReturnedPayments';
import styles from './ReturnedPaymentDialog.module.css';

const NEW_ACCOUNT = 'new';

const sameDigits = (a: string, b: string) => a.trim().replace(/^0+/, '') === b.trim().replace(/^0+/, '');

const isSameAccount = (account: PaymentInfoFormData, returned: BankAccountDtoIn) =>
  account.bankNumber === returned.bankNumber &&
  sameDigits(account.branchNumber, returned.branchNumber) &&
  sameDigits(account.accountNumber, returned.accountNumber);

const statusOf = (err: unknown) => (err as { response?: { status?: number } })?.response?.status;

interface ReturnedPaymentDialogProps {
  fundId: number;
  /** The deleted account to replace - a snapshot, so the dialog keeps its content once the list refreshes. */
  group: ReturnedPaymentDtoIn | null;
  onClose: () => void;
  /** After the requests changed - refresh whatever shows them. */
  onChanged: () => void;
}

/**
 * HAT-1395 «עדכון חשבון בנק». A payment to a supported's account bounced and the office deleted
 * the account; every request of the fund that still pays to it waits for another one.
 *
 * Built like «פרטי תשלום», the window where coordinators already manage a supported's accounts: the
 * same frame, the same way of showing an account, the same fields for a new one. On top of that it
 * says what saving will do to each request - which are approved again, the fund's balance permitting - and offers
 * the other way out the coordinators already use: a request filed again instead of fixed.
 */
const ReturnedPaymentDialog = ({ fundId, group, onClose, onChanged }: ReturnedPaymentDialogProps) => {
  const { t, i18n } = useTranslation();

  const [choice, setChoice] = useState('');
  const [newAccount, setNewAccount] = useState<PaymentInfoFormData>(createEmptyPaymentInfo);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fixed, setFixed] = useState<ReturnedPaymentFixResultDtoIn | null>(null);
  const [cancelled, setCancelled] = useState<number | null>(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  // Requests the coordinator leaves on the deleted account. Everything moves unless taken out.
  const [leftBehind, setLeftBehind] = useState<Set<string>>(() => new Set());

  const accountId = group?.returnedAccount.id ?? 0;
  const { mutateAsync: addAccount } = usePostAuthApi<PaymentMethodsDtoIn, BankAccountDetailsDtoOut[]>(
    group ? `funds/${fundId}/supporteds/${group.supportedId}/payment-infos` : ''
  );
  const { mutateAsync: fixAccount } = usePutAuthApi<ReturnedPaymentFixResultDtoIn, object, { paymentInfoId: number; correlationIds: string[] }>(
    fixReturnedPaymentUrl(fundId, accountId)
  );
  const { mutateAsync: cancelReturned } = usePostAuthApi<{ cancelled: number }, { correlationIds: string[] }>(cancelReturnedPaymentUrl(fundId, accountId));

  const handleClose = () => {
    setChoice('');
    setNewAccount(createEmptyPaymentInfo());
    setError(null);
    setFixed(null);
    setCancelled(null);
    setConfirmingCancel(false);
    setLeftBehind(new Set());
    onClose();
  };

  if (!group) return null;

  const months = group.requests.flatMap((request) => request.months);
  const returnedCount = months.filter((month) => month.returned).length;
  const hasReturned = returnedCount > 0;
  const moving = group.requests.filter((request) => !leftBehind.has(request.correlationId));
  const toggleRequest = (correlationId: string) =>
    setLeftBehind((current) => {
      const next = new Set(current);
      if (next.has(correlationId)) next.delete(correlationId);
      else next.add(correlationId);
      return next;
    });
  const toggleAll = () =>
    setLeftBehind(leftBehind.size === 0 ? new Set(group.requests.map((request) => request.correlationId)) : new Set());

  // With no other account there is nothing to pick - the form opens straight away.
  const selected = choice || (group.accounts.length === 0 ? NEW_ACCOUNT : '');
  const isNew = selected === NEW_ACCOUNT;
  const isNewComplete = !!(newAccount.accountOwnerName.trim() && newAccount.bankNumber && newAccount.branchNumber && newAccount.accountNumber);
  const repeatsReturnedAccount = isNew && isNewComplete && isSameAccount(newAccount, group.returnedAccount);
  const canSave = !isSaving && moving.length > 0 && (isNew ? isNewComplete && !repeatsReturnedAccount : !!selected);
  const isDone = fixed !== null || cancelled !== null;

  const monthLabel = (date: string) => new Date(date).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
  const amountLabel = (amount: number) => amount.toLocaleString(i18n.language, { style: 'currency', currency: 'ILS' });

  // One row per request, not per month: a monthly request on the account brings every month not
  // paid yet with it, and a row each made a window of 27 rows on the first real case.
  const monthsOf = (requestMonths: ReturnedPaymentMonthDtoIn[]) =>
    requestMonths.length === 1
      ? monthLabel(requestMonths[0].requestedForDate)
      : t('returnedPayment.monthRange', {
          from: monthLabel(requestMonths[0].requestedForDate),
          to: monthLabel(requestMonths[requestMonths.length - 1].requestedForDate),
          count: requestMonths.length,
        });
  const amountOf = (requestMonths: ReturnedPaymentMonthDtoIn[]) =>
    requestMonths.length === 1 || requestMonths.every((m) => m.amount === requestMonths[0].amount)
      ? requestMonths.length === 1
        ? amountLabel(requestMonths[0].amount)
        : t('returnedPayment.amountPerMonth', { amount: amountLabel(requestMonths[0].amount) })
      : amountLabel(requestMonths.reduce((sum, m) => sum + m.amount, 0));
  const returnedOf = (requestMonths: ReturnedPaymentMonthDtoIn[]) =>
    requestMonths.filter((m) => m.returned).map((m) => monthLabel(m.requestedForDate)).join(', ');
  // Said only on the rows where something is decided again - a column repeating «תעבור לחשבון
  // החדש» on every row hid the one row that was different. A monthly request splits: only the month
  // that bounced goes back, the months approved and not paid yet just move - counted, not named,
  // since the months column already says which one bounced.
  const backTagOf = (requestMonths: ReturnedPaymentMonthDtoIn[]) => {
    const back = requestMonths.filter((m) => m.goesBackForApproval);
    if (back.length === 0) return null;
    return back.length === requestMonths.length
      ? t('returnedPayment.backForApprovalTag')
      : t('returnedPayment.backForApprovalMonths', { count: back.length });
  };

  const handleSave = async () => {
    if (!canSave) return;
    setIsSaving(true);
    setError(null);

    let paymentInfoId = Number(selected);
    if (isNew) {
      try {
        const created = await addAccount([
          {
            type: PaymentMethodType.BankAccount,
            bankNumber: newAccount.bankNumber,
            branchNumber: newAccount.branchNumber,
            accountNumber: newAccount.accountNumber,
            accountOwnerName: newAccount.accountOwnerName.trim(),
            documents: [],
          },
        ]);
        paymentInfoId = created.accounts[0].id;
      } catch (err) {
        setError(statusOf(err) === 409 ? t('returnedPayment.accountAlreadyExists') : t('errorAddingPaymentInfo'));
        setIsSaving(false);
        return;
      }
    }

    try {
      setFixed(await fixAccount({ body: { paymentInfoId, correlationIds: moving.map((request) => request.correlationId) } }));
      onChanged();
    } catch (err) {
      // 409: someone already replaced it - nothing is left to do here.
      setError(statusOf(err) === 409 ? t('returnedPayment.alreadyFixed') : t('returnedPayment.saveFailed'));
      if (statusOf(err) === 409) onChanged();
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelReturned = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const result = await cancelReturned({ correlationIds: checkedBounced.map((request) => request.correlationId) });
      setCancelled(result.cancelled);
      onChanged();
    } catch (err) {
      setError(statusOf(err) === 409 ? t('returnedPayment.alreadyFixed') : t('returnedPayment.saveFailed'));
    } finally {
      setIsSaving(false);
      setConfirmingCancel(false);
    }
  };

  // The checked requests whose payment bounced - what «כבר הגשתי בקשה חדשה» cancels. The selection
  // drives both actions, so a request taken out of the list is not cancelled behind the coordinator's back.
  const checkedBounced = moving.filter((request) => request.months.some((month) => month.returned));
  const checkedReturnedMonths = checkedBounced.flatMap((request) => request.months).filter((month) => month.returned).length;
  const unpaidLeft = months.length - (cancelled ?? 0);
  // HAT-1395, the office's call: a month sent back is approved again when the fund's balance covers it.
  const approvedAgain = fixed?.requestsApprovedAgain ?? 0;
  const sentForApproval = fixed?.requestsSentForApproval ?? 0;

  return (
    <MainDialogLayout
      open
      onOpenChange={(isOpen: boolean) => !isOpen && handleClose()}
      title={t('returnedPayment.dialogTitle', { name: group.supportedName })}
      maxWidth="960px"
      fitContent
    >
      <div className={popupStyles.wrapper}>
        <div className={popupStyles.content}>
          {isDone ? (
            <div className={styles.done} role="status">
              <img src={successIcon} alt="" className={styles.doneIcon} />
              <p className="body-lr-semibold text-(--font-heading)">
                {fixed
                  ? approvedAgain > 0
                    ? t('returnedPayment.approvedTitle', { count: approvedAgain })
                    : sentForApproval === 0 && fixed.requestsMovedOnly > 0
                      ? t('returnedPayment.movedOnlyTitle', { count: fixed.requestsMovedOnly })
                      : t('returnedPayment.sentTitle', { count: sentForApproval })
                  : t('returnedPayment.cancelledTitle', { count: cancelled ?? 0 })}
              </p>
              {/* Approved again, mostly; the ones the fund's balance did not cover went to the office,
                  and are said so they do not look approved too. */}
              {fixed && approvedAgain > 0 && sentForApproval > 0 && (
                <p className="body-md-regular text-(--font-body-1)">{t('returnedPayment.sentAlso', { count: sentForApproval })}</p>
              )}
              {/* The requests that only moved are said too - otherwise the one that waited for its own
                  approval looks forgotten. */}
              {fixed && approvedAgain + sentForApproval > 0 && fixed.requestsMovedOnly > 0 && (
                <p className="body-md-regular text-(--font-body-1)">{t('returnedPayment.sentMovedOnly', { count: fixed.requestsMovedOnly })}</p>
              )}
              {fixed && leftBehind.size > 0 && (
                <p className="body-md-regular text-(--font-body-1)">{t('returnedPayment.leftBehindDone', { count: leftBehind.size })}</p>
              )}
              <p className="body-md-regular text-(--font-body-2)">
                {fixed
                  ? sentForApproval > 0 && approvedAgain === 0
                    ? t('returnedPayment.sentDescription')
                    : approvedAgain > 0 && sentForApproval === 0
                      ? t('returnedPayment.approvedDescription')
                      : ''
                  : unpaidLeft > 0 ? t('returnedPayment.cancelledUnpaidLeft', { count: unpaidLeft }) : ''}
              </p>
            </div>
          ) : (
            <>
              <section className={styles.section}>
                <h4 className="body-lr-semibold text-(--font-sub-title-2)">{t('returnedPayment.returnedAccountTitle')}</h4>
                <BankAccountLine
                  account={group.returnedAccount}
                  tag={<span className={`body-xs-sm-bold ${styles.deletedTag}`}>{t('returnedPayment.deletedTag')}</span>}
                />
              </section>

              {/* One list, not «returned» and «others» under two copies of the same headers: it is one
                  account and the requests on it. The bounced first - they are why it is gone. Each
                  row is checked by default; the coordinator takes out what should stay behind. */}
              <section className={styles.section}>
                <div className={styles.sectionHead}>
                  <h4 className="body-lr-semibold text-(--font-sub-title-2)">{t('returnedPayment.requestsTitle')}</h4>
                  <p className="body-sm-regular text-(--font-body-2)">{t('returnedPayment.requestsHint')}</p>
                </div>
                <div className={styles.tableScroller}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            className={styles.check}
                            checked={leftBehind.size === 0}
                            ref={(input) => {
                              if (input) input.indeterminate = leftBehind.size > 0 && moving.length > 0;
                            }}
                            onChange={toggleAll}
                            aria-label={t('returnedPayment.selectAll')}
                          />
                        </th>
                        <th className="body-xs-sm-bold">{t('returnedPayment.columnRequest')}</th>
                        <th className="body-xs-sm-bold">{t('returnedPayment.columnMonth')}</th>
                        <th className="body-xs-sm-bold">{t('returnedPayment.columnAmount')}</th>
                        <th className="body-xs-sm-bold">{t('returnedPayment.columnStatus')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.requests.map((request) => {
                        const shortId = getShortRequestId(request.correlationId);
                        const included = !leftBehind.has(request.correlationId);
                        const returned = returnedOf(request.months);
                        // The status the board shows, in the board's words and colour.
                        const status = getAggregatedStatus(request.months.map((month) => month.status));
                        const backTag = included ? backTagOf(request.months) : null;
                        return (
                          <tr key={request.correlationId} className={included ? undefined : styles.leftBehindRow}>
                            <td>
                              <input
                                type="checkbox"
                                className={styles.check}
                                checked={included}
                                onChange={() => toggleRequest(request.correlationId)}
                                aria-label={t('returnedPayment.selectRequest', { id: shortId })}
                              />
                            </td>
                            <td className="body-sm-regular">
                              <span className="block" dir="ltr">#{shortId}</span>
                              <span className="body-xs-sm-regular text-(--font-body-2)">{request.totalPayments > 1 ? t('monthly') : t('oneTime')}</span>
                            </td>
                            <td className="body-sm-regular">
                              <span className="block">{monthsOf(request.months)}</span>
                              {returned && request.months.length > 1 && (
                                <span className={`body-xs-sm-bold ${styles.returnedTag}`}>{t('returnedPayment.returnedIn', { months: returned })}</span>
                              )}
                            </td>
                            <td className="body-sm-regular">{amountOf(request.months)}</td>
                            <td className="body-sm-regular">
                              <span className={styles.status}>
                                <span className={styles.statusDot} style={{ '--status-color': getStatusColor(status) } as CSSProperties} />
                                {t(getStatusGroup(status)?.id ?? status)}
                              </span>
                              {backTag && <span className={`body-xs-sm-bold ${styles.backTag}`}>{backTag}</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="body-sm-regular text-(--font-body-2)">{t('returnedPayment.afterSaveNote')}</p>
                {leftBehind.size > 0 && (
                  <p className={`body-sm-medium ${styles.leftBehindNote}`} role="status">
                    {moving.length === 0 ? t('returnedPayment.noneSelected') : t('returnedPayment.leftBehind', { count: leftBehind.size })}
                  </p>
                )}
              </section>

              <div className={popupStyles.divider} />

              <fieldset className={styles.choices}>
                <legend className="body-lr-semibold text-(--font-sub-title-2)">{t('returnedPayment.pickAccount')}</legend>
                {group.accounts.map((account) => (
                  <label key={account.id} className={`${styles.option} ${selected === String(account.id) ? styles.optionSelected : ''}`}>
                    <input
                      type="radio"
                      name={`returned-payment-${group.returnedAccount.id}`}
                      checked={selected === String(account.id)}
                      onChange={() => setChoice(String(account.id))}
                    />
                    <BankAccountLine account={account} />
                  </label>
                ))}
                <label className={`${styles.option} ${isNew ? styles.optionSelected : ''}`}>
                  <input type="radio" name={`returned-payment-${group.returnedAccount.id}`} checked={isNew} onChange={() => setChoice(NEW_ACCOUNT)} />
                  <span className="body-lr-semibold text-(--font-body-1)">{t('returnedPayment.newAccount')}</span>
                </label>
                {isNew && (
                  <div className={styles.newAccount}>
                    <AddPaymentInfoForm single paymentInfos={[newAccount]} onPaymentInfosChange={(list) => setNewAccount(list[0])} />
                    {repeatsReturnedAccount && (
                      <p className="body-sm-regular text-(--font-error)" role="alert">
                        {t('returnedPayment.sameAsReturned')}
                      </p>
                    )}
                  </div>
                )}
              </fieldset>

              {error && (
                <p className="body-md-regular text-(--font-error)" role="alert">
                  {error}
                </p>
              )}
            </>
          )}
        </div>

        <div className={`${popupStyles.footer} ${styles.footer}`}>
          {isDone ? (
            <button type="button" onClick={handleClose} className={popupStyles.submitButton}>
              {t('close')}
            </button>
          ) : (
            <>
              {hasReturned ? (
                <button
                  type="button"
                  className={`body-md-semibold ${styles.secondaryAction}`}
                  onClick={() => setConfirmingCancel(true)}
                  disabled={isSaving || checkedBounced.length === 0}
                >
                  {t('returnedPayment.alreadyRefiled')}
                </button>
              ) : (
                <span />
              )}
              <button type="button" onClick={handleSave} disabled={!canSave} className={canSave ? popupStyles.submitButton : popupStyles.submitButtonDisabled}>
                {isSaving ? t('saving') : t('returnedPayment.moveSelected', { count: moving.length })}
              </button>
            </>
          )}
        </div>
      </div>

      <ConfirmationDialog
        open={confirmingCancel}
        onOpenChange={setConfirmingCancel}
        title={t('returnedPayment.cancelConfirmTitle')}
        message={t('returnedPayment.cancelConfirmMessage', { count: checkedReturnedMonths })}
        confirmText="returnedPayment.cancelConfirm"
        cancelText="cancel"
        onConfirm={handleCancelReturned}
        onCancel={() => setConfirmingCancel(false)}
        isLoading={isSaving}
        variant="danger"
      />
    </MainDialogLayout>
  );
};

export default ReturnedPaymentDialog;
